import { lazy, Suspense, useEffect, useState } from 'react';
import type {
  Player,
  CatRecord,
  ChallengeResult,
  ChallengeModifierId,
} from '@cradle7/shared';
import { useCatStore } from './catStore';
import { CatRetiredModal } from './ui/CatRetiredModal';

type AppProps = {
  initialPlayer?: Player | null;
};
import { useRunStore } from './store/runStore';
import { Intro, hasSeenIntro } from './ui/Intro';
import { MainMenu } from './ui/MainMenu';
import { PerkChoice } from './ui/PerkChoice';
import { HUD } from './ui/HUD';
import { EndScreen } from './ui/EndScreen';
import { Tutorial } from './ui/Tutorial';
import { saveRunRecord, generateRunId, getRunHistory, type RunOutcome } from './runHistory';
import { computeRunScore } from '@cradle7/shared';
import { useT, t, type TKey } from './i18n';
import { ToastContainer, pushToast, fireAchievement } from './ui/Toast';
import { WaveClearBanner } from './ui/WaveClearBanner';
import { InRunCoachmarks } from './ui/InRunCoachmarks';
import { EvolutionUnlockSubscriber } from './ui/EvolutionUnlockSubscriber';
import { OfflineBanner } from './ui/OfflineBanner';
import { RoomTransition } from './ui/RoomTransition';
import { ACHIEVEMENTS, unlockedSet } from './achievements';
import { refreshBadges } from './sectionBadges';
import { getCurrentSectorId, unlockNextOf } from './sectorStore';
import { api, setLastServerRunId, setLastFishGained } from './api';
import { hydrateFromServer, getAchievements } from './progressStore';
import { setDailyStatus } from './dailyStatusStore';
import { getActiveDailyRun, clearActiveDailyRun } from './dailyRunStore';
import { saveResume, loadResume, clearResume, hasResumableRun, scrubStaleChallengeMode } from './runResume';
import { ResumePrompt } from './ui/ResumePrompt';
import { BastetDialogOverlay } from './ui/BastetDialogOverlay';
import { BastetDefeatedScreen } from './ui/BastetDefeatedScreen';
import { ChallengeBadge } from './ui/ChallengeBadge';
import { ChallengeResultScreen } from './ui/ChallengeResultScreen';
import { getChallenge, hydrateChallenge } from './challengeStore';
import { enqueueSubmission } from './submitQueue';
import { useLoadoutStore } from './loadoutStore';
import { useConsumablesStore } from './consumablesStore';
import { AppHeader } from './ui/shell/AppHeader';
import { ParallaxBackground } from './ui/shell/ParallaxBackground';

// Lazy-load Phaser-heavy game canvas. Lives in its own chunk so MainMenu paints fast.
const PhaserGame = lazy(() =>
  import('./game/PhaserGame').then((m) => ({ default: m.PhaserGame })),

);

function PhaserFallback() {
  const { t } = useT();
  const [step, setStep] = useState(0);

  // Reveal one boot line every 220 ms. The user usually only sees 0-3 lines
  // because Phaser loads quickly, but on slow mobile they get the full sequence.
  useEffect(() => {
    const tm = setTimeout(() => setStep((s) => s + 1), 220);
    return () => clearTimeout(tm);
  }, [step]);

  const lines: Array<{ key: TKey; tone: 'primary' | 'secondary' | 'success' | 'warning' }> = [
    { key: 'loader.line.spinup',   tone: 'primary' },
    { key: 'loader.line.calibrate', tone: 'secondary' },
    { key: 'loader.line.uplink',    tone: 'primary' },
    { key: 'loader.line.decrypt',   tone: 'warning' },
    { key: 'loader.line.ready',     tone: 'success' },
  ];

  const visible = lines.slice(0, Math.min(step, lines.length));

  return (
    <div
      className="scanlines"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-6)',
        zIndex: 0,
      }}
    >
      <div className="scan-band" />

      <div
        className="label pulse glow-primary"
        style={{ fontSize: '0.7rem', letterSpacing: '0.2em', marginBottom: 'var(--space-3)' }}
      >
        {t('app.loading')}
      </div>

      <div
        style={{
          minWidth: '14rem',
          maxWidth: '20rem',
          width: '100%',
          fontFamily: 'inherit',
          fontSize: '0.7rem',
          lineHeight: 1.7,
          color: 'var(--fg-muted)',
        }}
      >
        {visible.map((line) => (
          <div key={line.key} className={`slide-in-left glow-${line.tone}`}>
            {t(line.key)}
          </div>
        ))}
        {step < lines.length && (
          <span className="blink" style={{ color: 'var(--fg-primary)' }}>_</span>
        )}
      </div>

      {/* Spinner — a horizontal pulse bar */}
      <div
        style={{
          marginTop: 'var(--space-3)',
          width: '14rem',
          height: 3,
          background: 'rgba(0, 8, 32, 0.6)',
          border: '1px solid var(--fg-primary-dim)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '40%',
            background: 'linear-gradient(90deg, transparent, var(--fg-primary), transparent)',
            animation: 'loader-slide 1.4s linear infinite',
          }}
        />
      </div>

      <div
        className="label dim"
        style={{ fontSize: '0.55rem', marginTop: 'var(--space-3)', opacity: 0.5 }}
      >
        {t('app.loadingDetail')}
      </div>
    </div>
  );
}

export function App({ initialPlayer: _initialPlayer }: AppProps = {}) {
  const [introDone, setIntroDone] = useState(() => hasSeenIntro());
  const phase = useRunStore((s) => s.phase);
  const challengeMode = useRunStore((s) => s.challengeMode);
  const [showResume, setShowResume] = useState(() => hasResumableRun());
  const [retiredCat, setRetiredCat] = useState<CatRecord | null>(null);
  // Phase 2h: gated cutscene shown once when the player first kills Bastet.
  // The check compares the freshly-returned bossDefeated against the cached
  // achievements snapshot taken BEFORE the submit-run round-trip.
  const [showBastetDefeat, setShowBastetDefeat] = useState(false);
  // Phase 2i: daily-challenge result overlay shown when submitRun resolves with
  // a `challenge` payload (i.e. the run was a valid challenge submission).
  const [showChallengeResult, setShowChallengeResult] = useState<{
    result: ChallengeResult;
    modifierId: ChallengeModifierId;
    totalPlayers: number;
  } | null>(null);


  // Phaser drives many sub-second state updates (HP, kills, etc.). Persisting on
  // every single change would thrash localStorage; we only need a snapshot at
  // each phase transition (in_combat → perk_choice → extracting / won / lost).
  useEffect(() => {
    return useRunStore.subscribe((state, prev) => {
      if (state.phase !== prev.phase) saveResume(state);
    });
  }, []);

  function doResume() {
    const saved = loadResume();
    if (saved) {
      // Phase 2i H2: a save from a previous UTC day still carries
      // challengeMode pointing at yesterday's challenge. Submitting that run
      // makes the server silent-drop the challenge payload (date mismatch),
      // so the player loses the entire run with no signal. Scrub stale
      // challengeMode → resume as a regular (non-challenge) run.
      const scrubbed = scrubStaleChallengeMode(saved);
      useRunStore.setState({ ...scrubbed });
    }
    setShowResume(false);
  }

  function doDiscard() {
    clearResume();
    setShowResume(false);
  }

  // Persist completed runs to localStorage on terminal phase transitions,
  // and fire achievement-unlock toasts for any newly-unlocked achievements.
  useEffect(() => {
    const unsub = useRunStore.subscribe((state, prev) => {
      const becameTerminal =
        (state.phase === 'won' || state.phase === 'lost') &&
        prev.phase !== state.phase &&
        prev.phase !== 'menu';
      if (!becameTerminal) return;
      // Guard against double-save if seed is 0 (uninitialized state).
      if (state.runStartedAt === 0) return;

      // If dying with at-risk loot, briefly surface how much was lost.
      // (lootAtRisk is already zeroed on the new state by damageCat; read from prev.)
      if (state.phase === 'lost' && prev.lootAtRisk > 0) {
        pushToast({
          variant: 'danger',
          title: t('loot.lost' as TKey).replace('{n}', String(prev.lootAtRisk)),
          durationMs: 3500,
        });
      }

      // Snapshot achievement set BEFORE save
      const beforeSet = unlockedSet(getRunHistory());

      const durationMs = Date.now() - state.runStartedAt;
      const clientScore = computeRunScore({
        outcome: state.phase as 'won' | 'lost',
        kills: state.enemiesKilled,
        loot: state.lootCount,
        durationMs,
      });

      saveRunRecord({
        id: generateRunId(),
        ts: Date.now(),
        outcome: state.phase as RunOutcome,
        kills: state.enemiesKilled,
        loot: state.lootCount,
        perks: [...state.ownedPerks],
        durationMs,
        seed: state.seed,
        score: clientScore,
        ...(state.bossDefeated ? { bossDefeated: state.bossDefeated } : {}),
      });

      // Fire-and-forget cloud submit. The local record is good enough for UI;
      // a server-side run will overwrite via the next /me/progress hydrate.
      const activeDaily = getActiveDailyRun();
      const dailyRunDate =
        activeDaily && activeDaily.seed === state.seed ? activeDaily.date : undefined;
      // Phase 2h: snapshot achievements BEFORE hydration so we can detect a
      // first-time Bastet kill (response shows bossDefeated === 'bastet' AND
      // cached snapshot still has bastetDefeated === false).
      const prevAchievements = getAchievements();
      const wasBastetKilled = state.bossDefeated === 'bastet';
      // Phase 2i: capture challengeMode BEFORE the async submit + resetToMenu so
      // we know whether to mount the ChallengeResultScreen on success. The mode
      // is wiped by startRun/resetToMenu, so we snapshot here.
      const challengeMode = state.challengeMode;
      void (async () => {
        try {
          const resp = await api.submitRun({
            seed: state.seed,
            outcome: state.phase as RunOutcome,
            kills: state.enemiesKilled,
            loot: state.lootCount,
            durationMs,
            perks: [...state.ownedPerks],
            clientScore,
            catClassId: state.selectedCatClassId,
            consumablesUsed: [...state.consumablesUsed],
            consumablesPicked: [...state.consumablesPicked],
            offeredSkipped: state.offeredSkippedLog.map((entry) => [...entry]),
            ...(state.bossDefeated ? { bossDefeated: state.bossDefeated } : {}),
            ...(dailyRunDate ? { dailyRunDate } : {}),
            ...(challengeMode
              ? { challengeRun: { date: challengeMode.date, modifierId: challengeMode.modifierId } }
              : {}),
          });
          setLastServerRunId(resp.run.id);
          setLastFishGained(resp.fishGained ?? 0);
          if (resp.consumables) {
            useConsumablesStore.getState().hydrateFromServer(resp.consumables);
          }
          if (resp.catUpdate) {
            useCatStore.getState().applyRunUpdate(resp.catUpdate);
            if (resp.catUpdate.retiredAt) {
              // Build a CatRecord shape from the update for the modal
              setRetiredCat({
                classId: resp.catUpdate.classId,
                livesRemaining: resp.catUpdate.livesRemaining,
                scars: resp.catUpdate.scars,
                mutations: resp.catUpdate.mutations,
                runsPlayed: resp.catUpdate.runsPlayed,
                createdAt: Date.now(),  // placeholder
                retiredAt: resp.catUpdate.retiredAt,
              });
            }
          }
          const progress = await api.meProgress().catch(() => null);
          if (progress) hydrateFromServer(progress);
          // Phase 2h: first-time Bastet kill triggers the defeat cutscene.
          // prevAchievements is the cache snapshot from BEFORE hydration, so
          // !prev.bastetDefeated means this run is the unlock.
          if (wasBastetKilled && !prevAchievements.bastetDefeated) {
            setShowBastetDefeat(true);
          }
          // Phase 2i: valid challenge submission → server returns `challenge`.
          // Show the result overlay and re-hydrate the challenge cache so the
          // Home card surfaces the fresh yourEntry/yourRank values.
          if (resp.challenge && challengeMode) {
            await hydrateChallenge();
            const cached = getChallenge();
            // Prefer leaderboard length from the freshly-hydrated cache; fall
            // back to `rank` so we never render "X / 0" (rank is always ≤ total).
            const totalPlayers = cached?.leaderboard.length ?? resp.challenge.rank;
            setShowChallengeResult({
              result: resp.challenge,
              modifierId: challengeMode.modifierId,
              totalPlayers: Math.max(totalPlayers, resp.challenge.rank),
            });
          }
          const daily = await api.meDaily().catch(() => null);
          if (daily) setDailyStatus(daily);
          // Refresh loadout — winning a run unlocks perks
          if (resp.run.outcome === 'won') {
            const loadout = await api.meLoadout().catch(() => null);
            if (loadout) useLoadoutStore.getState().hydrateFromServer(loadout);
          }
          if (dailyRunDate) clearActiveDailyRun();
          clearResume();
        } catch (err) {
          setLastServerRunId(null);
          setLastFishGained(0);
          enqueueSubmission({
            seed: state.seed,
            outcome: state.phase as 'won' | 'lost',
            kills: state.enemiesKilled,
            loot: state.lootCount,
            durationMs,
            perks: [...state.ownedPerks],
            clientScore,
            catClassId: state.selectedCatClassId,
            consumablesUsed: [...state.consumablesUsed],
            consumablesPicked: [...state.consumablesPicked],
            offeredSkipped: state.offeredSkippedLog.map((entry) => [...entry]),
            ...(state.bossDefeated ? { bossDefeated: state.bossDefeated } : {}),
            ...(dailyRunDate ? { dailyRunDate } : {}),
            ...(challengeMode
              ? { challengeRun: { date: challengeMode.date, modifierId: challengeMode.modifierId } }
              : {}),
          });
          console.warn('[submitRun] failed; enqueued for retry:', err);
        }
      })();

      // Now compute the AFTER set and find new unlocks
      const afterSet = unlockedSet(getRunHistory());
      for (const ach of ACHIEVEMENTS) {
        if (afterSet.has(ach.id) && !beforeSet.has(ach.id)) {
          fireAchievement(`${ach.icon} ${t(ach.nameKey)}`, t(ach.descKey), 4500);
        }
      }
      // Phase 3f — cascade unlock the next sector(s) gated by the just-won
      // sector. Fires an achievement toast for the first newly-unlocked id.
      // Opus #1: gate on !reactorMode && !challengeMode so Bastet boss wins
      // and daily-challenge wins don't mis-credit a sector unlock to whatever
      // currentSectorId happens to hold. Regular Home ▶ START still cascades
      // (currentSectorId reflects the last sector deploy or default 'B-1').
      if (state.phase === 'won' && !state.reactorMode && !state.challengeMode) {
        const winSector = getCurrentSectorId();
        const newUnlock = unlockNextOf(winSector);
        if (newUnlock) {
          fireAchievement(
            `★ ${t('sector.unlocked.title' as TKey)}`,
            t('sector.unlocked.body' as TKey).replace('{id}', newUnlock),
            3500,
          );
        }
      }
      refreshBadges();
    });
    return unsub;
  }, []);

  if (!introDone) {
    return (
      <>
        {showResume && <ResumePrompt onResume={doResume} onDiscard={doDiscard} />}
        <Intro onComplete={() => setIntroDone(true)} />
      </>
    );
  }

  // Closing the Bastet cutscene also clears reactorMode so the next regular ▶
  // launch (from Home) isn't accidentally treated as a reactor-mode run.
  const closeBastetDefeat = () => {
    setShowBastetDefeat(false);
    useRunStore.getState().setReactorMode(false);
  };

  // Phase 2i: closing the challenge-result overlay just unmounts it.
  // challengeMode itself is already wiped by startRun/resetToMenu — the
  // post-run flow (EndScreen → resetToMenu) already returned the player to
  // the menu by the time this overlay was shown.
  const closeChallengeResult = () => {
    setShowChallengeResult(null);
  };

  if (phase === 'menu') {
    return (
      <>
        {showResume && <ResumePrompt onResume={doResume} onDiscard={doDiscard} />}
        <ParallaxBackground />
        {!showBastetDefeat && !showChallengeResult && !retiredCat && <AppHeader />}
        <div style={{ paddingTop: 50, position: 'relative', zIndex: 1, height: '100%', minHeight: 0 }}><MainMenu /></div>
        {showBastetDefeat && <BastetDefeatedScreen onClose={closeBastetDefeat} />}
        {showChallengeResult && (
          <ChallengeResultScreen
            result={showChallengeResult.result}
            modifierId={showChallengeResult.modifierId}
            totalPlayers={showChallengeResult.totalPlayers}
            onClose={closeChallengeResult}
          />
        )}
        <CatRetiredModal cat={retiredCat} onDismiss={() => setRetiredCat(null)} />
        <ToastContainer />
        <OfflineBanner />
      </>
    );
  }

  const inRun = phase === 'in_combat' || phase === 'perk_choice' || phase === 'level_up' || phase === 'extracting';

  return (
    <>
      {showResume && <ResumePrompt onResume={doResume} onDiscard={doDiscard} />}
      <Suspense fallback={<PhaserFallback />}>
        <PhaserGame />
      </Suspense>
      <HUD />
      <RoomTransition />
      <EvolutionUnlockSubscriber />
      <WaveClearBanner />
      <InRunCoachmarks />
      {inRun && <Tutorial />}
      {inRun && <BastetDialogOverlay />}
      {inRun && challengeMode && <ChallengeBadge modifierId={challengeMode.modifierId} />}
      {(phase === 'perk_choice' || phase === 'level_up') && <PerkChoice />}
      {(phase === 'won' || phase === 'lost') && <EndScreen />}
      {showBastetDefeat && <BastetDefeatedScreen onClose={closeBastetDefeat} />}
      {showChallengeResult && (
        <ChallengeResultScreen
          result={showChallengeResult.result}
          modifierId={showChallengeResult.modifierId}
          totalPlayers={showChallengeResult.totalPlayers}
          onClose={closeChallengeResult}
        />
      )}
      <ToastContainer />
      <OfflineBanner />
      <CatRetiredModal cat={retiredCat} onDismiss={() => setRetiredCat(null)} />
    </>
  );
}
